import React, { useEffect, useMemo, useState } from 'react';
import {
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  TableActionTypes,
  useDataTable,
} from 'web-library';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { SubjectPaper } from './AccessArrangementRequestModels';

interface SubjectPaperDataTableProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  subjectPapersData: SubjectPaper[];
  modeOfAssessment?: string;
  requestTypeChange?: string;
  selectedSubjectPapers?: SubjectPaper[];
}

const SubjectPaperDataTable = ({
  control,
  setValue,
  subjectPapersData,
  modeOfAssessment,
  requestTypeChange,
  selectedSubjectPapers,
}: SubjectPaperDataTableProps) => {
  const [subjectPapers, setSubjectPapers] = useState(subjectPapersData);
  const [selectedIndexes, setSelectedIndexes] = useState<any[]>();
  const { state, dispatch: tableDispatch, tOptions, remoteTotalRowsSelected } = useDataTable();

  useEffect(() => {
    if (subjectPapers) {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: subjectPapers,
          count: subjectPapers.length,
        },
      } as TableActionTypes);
      tableDispatch({
        type: 'UPDATE_SIZE',
        payload: subjectPapers.length,
      } as TableActionTypes);
    }
  }, [subjectPapers, tableDispatch]);

  useEffect(() => {
    if (state.sortColumn && state.order) {
      const subjectPapersSorted = [...subjectPapers];

      subjectPapersSorted.sort((firstEl: any, secondEl: any) => {
        let order = 0;

        if (typeof firstEl[state.sortColumn] === 'string') {
          order = firstEl[state.sortColumn].localeCompare(secondEl[state.sortColumn]);
        } else if (typeof firstEl[state.sortColumn] === 'number') {
          order = firstEl[state.sortColumn] - secondEl[state.sortColumn];
        }

        return state.order === 'asc' ? order : order * -1;
      });

      setSubjectPapers(subjectPapersSorted);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state.sortColumn, state.order]);

  useEffect(() => {
    const subjectPapersSelected = Object.values(state.rowsSelected).map((subjectPaper) => ({
      examRegistrationDetailUuid: subjectPaper.examRegistrationDetailUuid,
      paperUuid: subjectPaper.paperUuid,
    }));

    selectedSubjectPapers?.forEach((selectedSubjectPaper) => {
      if (selectedSubjectPaper.dropped) {
        subjectPapersSelected.push(selectedSubjectPaper);
      }
    });

    setValue('subjectPapers', subjectPapersSelected);
  }, [setValue, state.rowsSelected, selectedSubjectPapers]);

  useEffect(() => {
    tableDispatch({
      type: 'CLEAR_SELECTION',
    } as TableActionTypes);
  }, [tableDispatch, modeOfAssessment, requestTypeChange]);

  useEffect(() => {
    if (selectedSubjectPapers) {
      const selectedPapers: { [key: string]: any } = {};
      const indexes: any[] = [];

      subjectPapers.forEach((subjectPaper, index) => {
        const selectedPaper = selectedSubjectPapers.find(
          ({ examRegistrationDetailUuid, paperUuid }) =>
            examRegistrationDetailUuid === subjectPaper.examRegistrationDetailUuid &&
            paperUuid === subjectPaper.paperUuid
        );

        if (selectedPaper) {
          selectedPapers[index] = {
            examRegistrationDetailUuid: selectedPaper.examRegistrationDetailUuid,
            paperUuid: selectedPaper.paperUuid,
          };

          indexes.push({ index, isDraft: selectedPaper.isDraft });
        }
      });

      setSelectedIndexes(indexes);

      tableDispatch({
        type: 'UPDATE_SELECTION',
        payload: selectedPapers,
      } as TableActionTypes);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tableDispatch, selectedSubjectPapers]);

  const tableColumns: MUIDataTableColumnDef[] = [
    {
      name: 'subject',
      label: 'Subject',
    },
    {
      name: 'examLevel',
      label: 'Exam Level',
    },
    {
      name: 'examSeries',
      label: 'Exam Series',
    },
    {
      name: 'subjectLevel',
      label: 'Subject Level',
    },
    {
      name: 'paperNumber',
      label: 'Paper No.',
    },
  ];

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      selectableRows: 'multiple',
      sort: remoteTotalRowsSelected === 0,
      isRowSelectable: (dataIndex: number) => {
        const selectedIndex: any = selectedIndexes?.find(
          (selected) => selected.index === dataIndex
        );

        return selectedIndex ? selectedIndex.isDraft : true;
      },
    }),
    [tOptions, remoteTotalRowsSelected, selectedIndexes]
  );

  return (
    <Controller
      control={control}
      name="subjectPapers"
      render={() => (
        <MUIDataTable
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns}
          data={state.data}
          options={customOptions}
        />
      )}
    />
  );
};

export default SubjectPaperDataTable;
